import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { CalendarClock, ChevronRight, AlertTriangle } from "lucide-react";
import { apiClient } from "@/lib/apiClient";

const fmt = (v) => new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }) : "–");

const TONE = {
  ok: { card: "border-sky-200 bg-sky-50/50", badge: "bg-sky-100 text-sky-800", count: "text-slate-900" },
  soon: { card: "border-amber-200 bg-amber-50/60", badge: "bg-amber-100 text-amber-800", count: "text-amber-700" },
  urgent: { card: "border-rose-200 bg-rose-50/60", badge: "bg-rose-100 text-rose-800", count: "text-rose-700" },
  overdue: { card: "border-rose-300 bg-rose-100/70", badge: "bg-rose-600 text-white", count: "text-rose-800" },
};

const countdown = (d) => {
  if (d === 0) return "heute fällig";
  if (d < 0) return `${-d} ${-d === 1 ? "Tag" : "Tage"} überfällig`;
  return `noch ${d} ${d === 1 ? "Tag" : "Tage"}`;
};

export default function VatDeadlineCard({ companyId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/vat/next-deadline`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !data.applicable) return null;
  const tone = TONE[data.status] || TONE.ok;
  const periodText = { monthly: "monatlich", quarterly: "quartalsweise", semiannual: "halbjährlich", annual: "jährlich mit Raten" }[data.reporting_period] || data.reporting_period;

  return (
    <Card
      className={`p-5 border cursor-pointer transition-colors hover:shadow-sm ${tone.card}`}
      onClick={() => navigate("/reports?tab=vat")}
      role="button"
      data-testid="dashboard-vat-deadline-card"
      data-status={data.status}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-slate-700" />
          <h3 className="text-sm font-semibold text-slate-900">Nächste MWST-Frist · {data.period_label}</h3>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.badge}`} data-testid="vat-deadline-status">
          {data.status === "overdue" ? "Überfällig" : data.status === "urgent" ? "Dringend" : data.status === "soon" ? "Bald fällig" : "Im Plan"}
        </span>
      </div>
      <div className="mt-3 flex items-end justify-between gap-4">
        <div>
          <div className={`text-2xl font-bold ${tone.count}`} data-testid="vat-deadline-countdown">
            {data.status === "overdue" && <AlertTriangle className="inline h-5 w-5 mr-1 -mt-1" />}
            {countdown(data.days_left)}
          </div>
          <div className="mt-0.5 text-xs text-slate-600" data-testid="vat-deadline-date">
            Abgabe & Zahlung bis {fmtDate(data.deadline)} · Periode {fmtDate(data.period_start)}–{fmtDate(data.period_end)} · {periodText}
            {data.liability != null && (
              <> · provisorische Zahllast <span className="font-semibold text-slate-800" data-testid="vat-deadline-liability">{fmt(data.liability)}</span></>
            )}
          </div>
          {data.next?.deadline && (
            <div className="mt-0.5 text-[11px] text-slate-500" data-testid="vat-deadline-next">
              Danach: {data.next.period_label} bis {fmtDate(data.next.deadline)}
            </div>
          )}
          {data.installments?.next && (
            <div className={`mt-1 text-[11px] ${data.installments.next.status === "overdue" ? "text-rose-700 font-medium" : "text-slate-600"}`} data-testid="vat-deadline-installment">
              Nächste Rate: {data.installments.next.label} bis {fmtDate(data.installments.next.due_date)}{data.installments.next.amount != null && <> · {fmt(data.installments.next.amount)}</>} · {data.installments.open} offen
            </div>
          )}
          {data.period_closed && (
            <div className="mt-1 text-[11px] text-slate-500" data-testid="vat-deadline-hint">
              Frist gilt als erledigt, sobald die MWST-Abschlussbuchung für diese Periode im MWST-Tab erzeugt ist.
            </div>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 whitespace-nowrap">
          Abrechnung <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Card>
  );
}
